import { useEffect, useState } from "react";
import api from "@/lib/api";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import PageHeader from "@/components/dashboard/PageHeader";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import { fireFrontendSentryTest } from "@/sentry/reporters";

export default function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [articles, setArticles] = useState([]);
  const [billing, setBilling] = useState(null);
  const [sentryStatus, setSentryStatus] = useState(null);
  const [sentryBusy, setSentryBusy] = useState(false);
  const [sentryResult, setSentryResult] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/users").then(({ data }) => setUsers(data));
    api.get("/admin/projects").then(({ data }) => setProjects(data));
    api.get("/admin/articles").then(({ data }) => setArticles(data));
    api.get("/admin/billing").then(({ data }) => setBilling(data));
    api.get("/admin/sentry/status").then(({ data }) => setSentryStatus(data)).catch(() => {});
  }, []);

  const runSentryTest = async () => {
    setSentryBusy(true);
    setSentryResult(null);
    try {
      const { data: be } = await api.post("/admin/sentry/test", {});
      const feEventId = fireFrontendSentryTest();
      let feAck = null;
      if (feEventId) {
        try {
          const { data } = await api.post("/admin/sentry/verify-frontend", {
            event_id: feEventId,
            environment: process.env.REACT_APP_SENTRY_ENVIRONMENT,
            release: process.env.REACT_APP_SENTRY_RELEASE,
          });
          feAck = data;
        } catch (_e) {
          /* backend record failed; still show the fe event id */
        }
      }
      const { data: status } = await api.get("/admin/sentry/status");
      setSentryStatus(status);
      setSentryResult({ backend: be, frontend_event_id: feEventId, frontend_ack: feAck });
      if (be?.status === "configuration_required") {
        toast.message("Backend DSN not configured on this environment.");
      } else if (feEventId && be?.event_id) {
        toast.success("Sentry test events fired — check your Sentry dashboard.");
      } else if (!feEventId) {
        toast.message("Frontend DSN not configured. Set REACT_APP_SENTRY_DSN in Emergent Secrets & rebuild.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sentry test failed.");
    } finally {
      setSentryBusy(false);
    }
  };

  const runEmailTest = async () => {
    if (!emailTo || !emailTo.includes("@")) {
      toast.error("Enter a valid email address");
      return;
    }
    setEmailBusy(true);
    try {
      const { data } = await api.post("/admin/email/test", { to: emailTo });
      toast.success(`Resend status: ${data.status}${data.provider_id ? ` · id ${data.provider_id.slice(0, 8)}…` : ""}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Email test failed.");
    } finally {
      setEmailBusy(false);
    }
  };

  return (
    <DashboardLayout>
      <PageHeader overline="Admin" title="Operations panel" description="Users, projects, content review, integrations, billing." />
      <Tabs defaultValue="users" className="w-full">
        <TabsList className="bg-white border border-border rounded-sm">
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="projects" data-testid="admin-tab-projects">Projects</TabsTrigger>
          <TabsTrigger value="content" data-testid="admin-tab-content">Content</TabsTrigger>
          <TabsTrigger value="billing" data-testid="admin-tab-billing">Billing</TabsTrigger>
          <TabsTrigger value="ops" data-testid="admin-tab-ops">Ops</TabsTrigger>
        </TabsList>
        <TabsContent value="users" className="mt-4">
          <div className="bg-white border border-border rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-ivory-100 text-left text-[11px] uppercase tracking-widest text-muted-foreground">
                <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Onboarded</th></tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-border" data-testid={`admin-user-${u.id}`}>
                    <td className="px-4 py-3 font-medium">{u.name}</td>
                    <td className="px-4 py-3">{u.email}</td>
                    <td className="px-4 py-3 capitalize">{u.role}</td>
                    <td className="px-4 py-3">{u.onboarding_completed ? "Yes" : "No"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="projects" className="mt-4">
          <div className="bg-white border border-border rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-ivory-100 text-left text-[11px] uppercase tracking-widest text-muted-foreground">
                <tr><th className="px-4 py-3">Project</th><th className="px-4 py-3">Website</th><th className="px-4 py-3">Model</th></tr>
              </thead>
              <tbody>
                {projects.map((p) => (
                  <tr key={p.id} className="border-t border-border" data-testid={`admin-project-${p.id}`}>
                    <td className="px-4 py-3 font-medium">{p.name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{p.website_url}</td>
                    <td className="px-4 py-3">{p.business_model}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="content" className="mt-4">
          <div className="bg-white border border-border rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-ivory-100 text-left text-[11px] uppercase tracking-widest text-muted-foreground">
                <tr><th className="px-4 py-3">Keyword</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Created</th></tr>
              </thead>
              <tbody>
                {articles.map((a) => (
                  <tr key={a.id} className="border-t border-border" data-testid={`admin-article-${a.id}`}>
                    <td className="px-4 py-3 font-medium">{a.target_keyword}</td>
                    <td className="px-4 py-3">{a.content_type}</td>
                    <td className="px-4 py-3">{a.status}</td>
                    <td className="px-4 py-3 text-xs">{a.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="billing" className="mt-4">
          {billing && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat label="Active users" value={billing.active_users} />
              <Stat label="Active projects" value={billing.active_projects} />
              <Stat label="MRR estimate" value={`$${billing.mrr_estimate}`} sub="placeholder" />
              <Stat label="Stripe" value={billing.stripe} />
            </div>
          )}
        </TabsContent>

        <TabsContent value="ops" className="mt-4 space-y-6">
          <div className="bg-white border border-border rounded-sm p-6" data-testid="ops-sentry-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="editorial-overline">Ops · Sentry</p>
                <h3 className="font-serif text-2xl mt-1">Sentry verification</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                  Fires one backend event via <code>/api/admin/sentry/test</code> and one frontend event via the
                  browser SDK. Sentry stays <em>configuration_required</em> until both events land.
                </p>
              </div>
              <button
                onClick={runSentryTest}
                disabled={sentryBusy}
                data-testid="admin-sentry-test-btn"
                className="px-4 py-2 bg-navy-900 hover:bg-navy-800 text-ivory-50 font-semibold rounded-sm text-sm disabled:opacity-50"
              >
                {sentryBusy ? "Firing…" : "Fire Sentry test events"}
              </button>
            </div>
            {sentryStatus && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5 text-sm" data-testid="admin-sentry-status">
                <Stat label="Backend DSN" value={sentryStatus.backend_dsn_configured ? "Configured" : "Missing"} />
                <Stat label="Backend init" value={sentryStatus.backend_initialized ? "OK" : "Off"} />
                <Stat label="Environment" value={sentryStatus.environment || "—"} />
                <Stat label="Release" value={sentryStatus.release || "—"} sub={sentryStatus.connected ? "Verified" : "Awaiting events"} />
              </div>
            )}
            {sentryResult && (
              <pre className="mt-4 text-xs font-mono bg-ivory-100 p-3 rounded-sm overflow-auto" data-testid="admin-sentry-result">
                {JSON.stringify(sentryResult, null, 2)}
              </pre>
            )}
          </div>

          <div className="bg-white border border-border rounded-sm p-6" data-testid="ops-resend-card">
            <p className="editorial-overline">Ops · Resend</p>
            <h3 className="font-serif text-2xl mt-1">Send a test email</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-xl">
              Delivers a real transactional email via Resend. Idempotency key changes each minute.
            </p>
            <div className="flex gap-2 mt-4 max-w-md">
              <input
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="you@example.com"
                data-testid="admin-email-input"
                className="flex-1 border border-border rounded-sm px-3 py-2 text-sm"
              />
              <button
                onClick={runEmailTest}
                disabled={emailBusy}
                data-testid="admin-email-test-btn"
                className="px-4 py-2 bg-gold-500 hover:bg-gold-400 text-navy-900 font-semibold rounded-sm text-sm disabled:opacity-50"
              >
                {emailBusy ? "Sending…" : "Send test"}
              </button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}
function Stat({ label, value, sub }) {
  return (
    <div className="bg-white border border-border rounded-sm p-5">
      <p className="editorial-overline">{label}</p>
      <p className="font-serif text-3xl mt-1">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}
